// TeamTaskNotification.jsx
import React, { useState, useEffect } from "react";
import { X, CheckCircle, Clock, AlertCircle, Users } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";

const TeamTaskNotification = ({ task, onClose, onView }) => {
  const [isVisible, setIsVisible] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  useEffect(() => {
    setIsVisible(true);

    const timer = setTimeout(() => {
      handleClose();
    }, 100000);

    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300);
  };

  const handleView = () => {
    onView(task.id);
    handleClose();
  };

  const getPriorityColor = (priority) => {
    const colors = {
      low: "border-gray-500 bg-gray-500/10",
      medium: "border-yellow-500 bg-yellow-500/10",
      high: "border-orange-500 bg-orange-500/10",
      urgent: "border-red-500 bg-red-500/10",
    };
    return colors[priority] || colors.medium;
  };

  const getTypeIcon = (type) => {
    const icons = {
      urgent: AlertCircle,
      assignment: Users,
      request: Clock,
      approval: CheckCircle,
      notification: AlertCircle,
      general: Users,
    };
    return icons[type] || Users;
  };

  const TypeIcon = getTypeIcon(task.type);

  return (
    <div
      className={`fixed top-4 right-4 z-[99999] transition-all duration-300 transform ${
        isVisible
          ? "translate-x-0 opacity-100 scale-100"
          : "translate-x-full opacity-0 scale-95"
      }`}
      style={{ maxWidth: "480px", width: "calc(100vw - 2rem)" }}
    >
      <div
        className={`rounded-xl z-[9999] border-2 shadow-2xl ${getPriorityColor(
          task.priority,
        )} overflow-hidden`}
        style={{ backgroundColor: isDarkMode ? "#0C0E12" : "#FFFFFF" }}
      >
        {/* Header with flash effect */}
        <div
          className={`px-6 py-4 border-b ${
            task.priority === "urgent"
              ? "border-red-500/30 bg-red-500/5 animate-pulse"
              : isDarkMode
                ? "border-gray-700"
                : "border-gray-200"
          }`}
        >
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3 flex-1">
              <div
                className={`mt-1 p-2 rounded-lg ${
                  task.priority === "urgent"
                    ? "bg-red-500/20 text-red-400"
                    : task.priority === "high"
                      ? "bg-orange-500/20 text-orange-400"
                      : "bg-blue-500/20 text-blue-400"
                }`}
              >
                <TypeIcon size={20} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3
                    className={`font-semibold text-lg truncate ${isDarkMode ? "text-white" : "text-gray-900"}`}
                  >
                    New Team Task
                  </h3>
                  {task.priority === "urgent" && (
                    <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded uppercase animate-pulse">
                      Urgent
                    </span>
                  )}
                </div>
                <p
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  From {task.creator?.name || "Unknown"}
                </p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className={`transition-colors p-1 ml-2 ${isDarkMode ? "text-gray-400 hover:text-white" : "text-gray-500 hover:text-gray-700"}`}
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-4">
          <h4
            className={`font-semibold mb-2 text-base ${isDarkMode ? "text-white" : "text-gray-900"}`}
          >
            {task.title}
          </h4>
          {task.description && (
            <p
              className={`text-sm mb-3 line-clamp-2 ${isDarkMode ? "text-gray-300" : "text-gray-600"}`}
            >
              {task.description}
            </p>
          )}

          {/* Task Details */}
          <div
            className={`flex flex-wrap gap-3 text-xs mb-4 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
          >
            {task.dueDate && (
              <div className="flex items-center gap-1">
                <Clock size={14} />
                <span>Due: {new Date(task.dueDate).toLocaleDateString()}</span>
              </div>
            )}
            {task.type && (
              <div className="flex items-center gap-1">
                <span className="capitalize">{task.type}</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <button
              onClick={handleView}
              className="flex-1 px-4 py-2.5 bg-[#427BCA] hover:bg-[#5A9BE8] text-white rounded-lg font-medium transition-colors"
            >
              View Details
            </button>
            <button
              onClick={handleClose}
              className={`px-4 py-2.5 rounded-lg font-medium transition-colors ${isDarkMode ? "bg-[#13161B] hover:bg-gray-700 text-gray-300" : "bg-gray-100 hover:bg-gray-200 text-gray-600"}`}
            >
              Later
            </button>
          </div>
        </div>

        {/* Progress bar animation */}
        <div
          className={`h-1 overflow-hidden ${isDarkMode ? "bg-gray-800" : "bg-gray-100"}`}
        >
          <div
            className={`h-full ${
              task.priority === "urgent" ? "bg-red-500" : "bg-[#427BCA]"
            } animate-progress`}
            style={{
              animation: "progress 10s linear forwards",
            }}
          />
        </div>
      </div>

      <style jsx>{`
        @keyframes progress {
          from {
            width: 100%;
          }
          to {
            width: 0%;
          }
        }
      `}</style>
    </div>
  );
};

export default TeamTaskNotification;
